import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";

import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { useSession } from "@/hooks/use-session";
import {
  runSiteAudit,
  runSiteAuditFull,
  type AuditReport,
} from "@/lib/site-audit.functions";

const FAQS = [
  {
    q: "Where does the audit data come from?",
    a: "Every report is a live run from Google's PageSpeed Insights service. The scores, Core Web Vitals and failed checks are returned by Google — we do not estimate or invent any figure.",
  },
  {
    q: "Why does the audit take 20–30 seconds?",
    a: "Google loads your page in a real browser, records how it renders, and runs more than a hundred checks before returning the result. That render is what makes the numbers trustworthy.",
  },
  {
    q: "What is a good audit score?",
    a: "90–100 is good, 50–89 needs work, and below 50 is poor. Performance moves the most because it depends on your hosting, images and scripts, while SEO and accessibility are usually quick wins.",
  },
  {
    q: "Why can I only audit one website for free?",
    a: "Each free account gets an unlimited number of audits for a single website, so the tool stays fast for everyone. If you manage several sites, contact us and we will run a multi-site audit for you.",
  },
  {
    q: "Will fixing these issues improve my rankings?",
    a: "Page experience is one ranking signal among many. Speed and accessibility fixes help, but they work best alongside strong content and keyword coverage — see our keyword explorer to find the terms worth targeting.",
  },
];

function ring(score: number | null) {
  if (score === null) return "text-brand-deep/30";
  if (score >= 90) return "text-emerald-600";
  if (score >= 50) return "text-amber-600";
  return "text-rose-600";
}

function ringBg(score: number | null) {
  if (score === null) return "border-brand-deep/10";
  if (score >= 90) return "border-emerald-500/40 bg-emerald-50/50";
  if (score >= 50) return "border-amber-500/40 bg-amber-50/50";
  return "border-rose-500/40 bg-rose-50/50";
}

const VERDICT_STYLE: Record<string, string> = {
  good: "bg-emerald-100 text-emerald-700",
  "needs-work": "bg-amber-100 text-amber-700",
  poor: "bg-rose-100 text-rose-700",
};

const VERDICT_LABEL: Record<string, string> = {
  good: "Pass",
  "needs-work": "Needs work",
  poor: "Fail",
};

export const Route = createFileRoute("/tools/website-audit")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/tools/website-audit" }],
    meta: [
      { property: "og:url", content: "https://mgcontentlabs.com/tools/website-audit" },
      { title: "Free SEO Score Checker & Website Audit Tool (India) | MG Content Labs" },
      {
        name: "description",
        content:
          "Free SEO score checker: run a live Google audit on any page for performance, accessibility, best practices and SEO scores, Core Web Vitals and fixes.",
      },
      { property: "og:title", content: "Free SEO Score Checker & Website Audit Tool" },
      {
        property: "og:description",
        content:
          "Real Google PageSpeed scores, Core Web Vitals and prioritised fixes for any page. Free, no credit card.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "MG Content Labs Website Audit",
          url: "https://mgcontentlabs.com/tools/website-audit",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          creator: { "@type": "Organization", name: "MG Content Labs" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: WebsiteAudit,
});

function WebsiteAudit() {
  const { user, loading: sessionLoading } = useSession();
  const auditPublic = useServerFn(runSiteAudit);
  const auditFull = useServerFn(runSiteAuditFull);

  const [url, setUrl] = useState("");
  const [strategy, setStrategy] = useState<"mobile" | "desktop">("mobile");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<AuditReport | null>(null);
  const [copied, setCopied] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, AuditReport["failed"]>();
    (report?.failed ?? []).forEach((f) => {
      const list = map.get(f.category) ?? [];
      list.push(f);
      map.set(f.category, list);
    });
    return [...map.entries()];
  }, [report]);

  async function handleRun(event: React.FormEvent) {
    event.preventDefault();
    if (!url.trim() || running) return;
    setRunning(true);
    setError(null);
    setReport(null);
    setCopied(false);
    try {
      const payload = { data: { url: url.trim(), strategy } };
      const result = user ? await auditFull(payload) : await auditPublic(payload);
      if (result.ok) setReport(result.report);
      else setError(result.error);
    } catch {
      setError("Something went wrong running that audit. Please try again.");
    } finally {
      setRunning(false);
    }
  }

  function copySummary() {
    if (!report) return;
    const lines = [
      `Website audit — ${report.finalUrl} (${report.strategy})`,
      `Performance ${report.scores.performance ?? "—"} | Accessibility ${report.scores.accessibility ?? "—"} | Best Practices ${report.scores.bestPractices ?? "—"} | SEO ${report.scores.seo ?? "—"}`,
      "",
      "Core Web Vitals:",
      ...report.vitals.map((v) => `- ${v.label}: ${v.display} (${VERDICT_LABEL[v.verdict]})`),
      "",
      "Issues to fix:",
      ...report.failed.map((f) => `- [${f.category}] ${f.title}${f.display ? ` — ${f.display}` : ""}`),
    ];
    void navigator.clipboard.writeText(lines.join("\n"));
    setCopied(true);
  }

  return (
    <>
      <SiteNav />
      <main className="bg-white">
        <section className="border-b border-brand-deep/5 bg-brand-muted/40 py-16">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-brand-accent">Free Tool</p>
            <h1 className="text-4xl font-bold tracking-tight text-brand-deep md:text-5xl">Free SEO Score Checker</h1>
            <p className="mx-auto mt-4 max-w-xl text-brand-deep/70">
              Run a live Google website audit on any page. Real performance,
              accessibility, best practices and SEO scores — plus the exact issues slowing you down.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto max-w-4xl px-6">
            <form onSubmit={handleRun} className="rounded-2xl border border-brand-deep/10 bg-white p-6 shadow-sm">
              <label htmlFor="audit-url" className="mb-2 block text-sm font-semibold text-brand-deep">
                Website address
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  id="audit-url"
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="e.g. mgcontentlabs.com"
                  className="w-full rounded-xl border border-brand-deep/15 bg-white px-4 py-3 text-base text-brand-deep outline-none focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20"
                />
                <button
                  type="submit"
                  disabled={running}
                  className="shrink-0 rounded-xl bg-brand-accent px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
                >
                  {running ? "Auditing…" : "Run audit"}
                </button>
              </div>

              <div className="mt-4 flex items-center gap-2">
                {(["mobile", "desktop"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStrategy(s)}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize transition ${
                      strategy === s
                        ? "bg-brand-deep text-white"
                        : "border border-brand-deep/15 text-brand-deep/70 hover:border-brand-accent/40"
                    }`}
                  >
                    {s}
                  </button>
                ))}
                <span className="ml-auto text-xs text-brand-deep/50">Takes 20–30 seconds</span>
              </div>
            </form>

            {running && (
              <p className="mt-6 animate-pulse text-center text-sm text-brand-deep/60">
                Running your free SEO score check — performance, accessibility, best practices and SEO audit…
              </p>
            )}

            {error && (
              <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                {error}{" "}
                {error.includes("multi-site") && (
                  <Link to="/contact" className="font-semibold underline">
                    Contact us
                  </Link>
                )}
              </div>
            )}

            {report && (
              <div className="mt-10 space-y-10">
                <div>
                  <h2 className="text-sm font-bold uppercase tracking-widest text-brand-deep">
                    Your SEO & page quality scores
                  </h2>
                  <p className="mt-1 text-xs text-brand-deep/55">
                    {report.finalUrl} · {report.strategy} · live data from Google PageSpeed Insights
                  </p>
                  <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    {[
                      { label: "Performance", value: report.scores.performance },
                      { label: "Accessibility", value: report.scores.accessibility },
                      { label: "Best Practices", value: report.scores.bestPractices },
                      { label: "SEO", value: report.scores.seo },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className={`rounded-2xl border p-5 text-center ${ringBg(s.value)}`}
                      >
                        <div className={`text-4xl font-bold tabular-nums ${ring(s.value)}`}>
                          {s.value ?? "—"}
                        </div>
                        <div className="mt-1 text-xs font-semibold text-brand-deep/70">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {!user && !sessionLoading && (
                  <div className="relative overflow-hidden rounded-2xl border border-brand-deep/10">
                    <div className="pointer-events-none select-none blur-[5px]" aria-hidden="true">
                      <div className="space-y-3 p-6">
                        {["Largest Contentful Paint", "Cumulative Layout Shift", "Total Blocking Time"].map((l) => (
                          <div key={l} className="flex items-center justify-between border-b border-brand-deep/5 pb-3">
                            <span className="text-sm text-brand-deep/70">{l}</span>
                            <span className="h-4 w-16 rounded bg-brand-deep/15" />
                          </div>
                        ))}
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="h-10 rounded bg-brand-deep/10" />
                        ))}
                      </div>
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/75 p-6 text-center">
                      <p className="text-lg font-bold text-brand-deep">See your full report</p>
                      <p className="mt-1 max-w-sm text-sm text-brand-deep/70">
                        Create a free account to unlock Core Web Vitals, every failed check and the
                        prioritised fixes for your site.
                      </p>
                      <Link
                        to="/auth"
                        search={{ next: "/tools/website-audit" }}
                        className="mt-4 rounded-full bg-brand-accent px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                      >
                        Create a free account
                      </Link>
                    </div>
                  </div>
                )}

                {user && report.vitals.length > 0 && (
                  <div>
                    <h2 className="text-sm font-bold uppercase tracking-widest text-brand-deep">
                      Core Web Vitals
                    </h2>
                    <div className="mt-4 divide-y divide-brand-deep/5 rounded-2xl border border-brand-deep/10">
                      {report.vitals.map((v) => (
                        <div key={v.id} className="flex items-center justify-between gap-4 p-4">
                          <span className="text-sm font-medium text-brand-deep">{v.label}</span>
                          <span className="flex items-center gap-3">
                            <span className="text-sm font-semibold tabular-nums text-brand-deep">{v.display}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${VERDICT_STYLE[v.verdict]}`}>
                              {VERDICT_LABEL[v.verdict]}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {user && report.weight && (
                  <div>
                    <h2 className="text-sm font-bold uppercase tracking-widest text-brand-deep">Page weight</h2>
                    <div className="mt-4 grid grid-cols-3 gap-4">
                      {[
                        { label: "Total", value: report.weight.total },
                        { label: "Images", value: report.weight.images },
                        { label: "Scripts", value: report.weight.scripts },
                      ].map((w) => (
                        <div key={w.label} className="rounded-2xl border border-brand-deep/10 p-4 text-center">
                          <div className="text-xl font-bold tabular-nums text-brand-deep">{w.value}</div>
                          <div className="text-xs text-brand-deep/60">{w.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {user && grouped.length > 0 && (
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h2 className="text-sm font-bold uppercase tracking-widest text-brand-deep">
                        Issues to fix ({report.failed.length})
                      </h2>
                      <button
                        type="button"
                        onClick={copySummary}
                        className="rounded-full border border-brand-deep/15 px-4 py-1.5 text-xs font-semibold text-brand-deep/70 transition hover:border-brand-accent/40 hover:text-brand-accent"
                      >
                        {copied ? "Copied ✓" : "Copy summary"}
                      </button>
                    </div>
                    <div className="mt-4 space-y-6">
                      {grouped.map(([category, items]) => (
                        <div key={category}>
                          <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-brand-accent">
                            {category}
                          </h3>
                          <div className="divide-y divide-brand-deep/5 rounded-2xl border border-brand-deep/10">
                            {items.map((f) => (
                              <div key={f.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                  <p className="text-sm font-semibold text-brand-deep">{f.title}</p>
                                  {f.display && (
                                    <span className="shrink-0 text-xs font-semibold tabular-nums text-rose-600">
                                      {f.display}
                                    </span>
                                  )}
                                </div>
                                {f.description && (
                                  <p className="mt-1 text-xs leading-relaxed text-brand-deep/65">{f.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {user && report.failed.length === 0 && report.vitals.length > 0 && (
                  <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                    No failing checks found — this page is in good shape.
                  </p>
                )}
              </div>
            )}

            <div className="mt-14 rounded-2xl bg-brand-deep p-8 text-center">
              <h2 className="text-2xl font-bold text-white">Want us to fix these issues?</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-white/70">
                We handle technical SEO, page speed and content together, so the fixes actually show up
                in rankings.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Link
                  to="/services"
                  className="rounded-full bg-brand-accent px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  Explore Our Services
                </Link>
                <Link
                  to="/pricing"
                  className="rounded-full border border-white/30 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  View Pricing Plans
                </Link>
              </div>
            </div>

            <div className="mt-14">
              <h2 className="text-2xl font-bold text-brand-deep">Website audit FAQs</h2>
              <div className="mt-5 space-y-4">
                {FAQS.map((f) => (
                  <div key={f.q} className="rounded-2xl border border-brand-deep/10 p-5">
                    <h3 className="text-sm font-bold text-brand-deep">{f.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-brand-deep/70">
                      {f.a}{" "}
                      {f.q.startsWith("Will fixing") && (
                        <Link to="/tools/keyword-explorer" className="font-semibold text-brand-accent hover:underline">
                          Try the Keyword Explorer →
                        </Link>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
